import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "../../lib/api";
import { cn } from "../../lib/cn";

type TodayData = {
  tenant_name: string;
  date: string;
  counts: {
    pending_confirmation: number;
    confirmed: number;
    completed: number;
    cancelled: number;
    no_show: number;
  };
  appointments: Array<{
    id: string;
    customer_name?: string | null;
    service_name?: string | null;
    staff_name?: string | null;
    start_at: string;
    status: string;
    allowed_actions: string[];
  }>;
};

const STATUS_LABELS: Record<string, string> = {
  pending_confirmation: "Pending",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
  no_show: "No-show",
};

const ACTION_LABELS: Record<string, string> = {
  confirm: "Confirm ✅",
  cancel: "Cancel ❌",
  complete: "Complete ✔",
  no_show: "No-show 👻",
  reschedule: "Reschedule 🔁",
};

const STATUS_COLORS: Record<string, string> = {
  pending_confirmation: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  completed: "bg-blue-100 text-blue-800",
  cancelled: "bg-gray-100 text-gray-600",
  no_show: "bg-red-100 text-red-700",
};

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function TgTodayPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery<TodayData>({
    queryKey: ["tg-today"],
    queryFn: () => apiGet("/v3/tg/today"),
    refetchInterval: 30_000,
  });

  const transition = useMutation({
    mutationFn: ({ id, action }: { id: string; action: string }) =>
      apiPost(`/v3/tg/appointments/${id}/transition`, { action }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tg-today"] }),
  });

  if (isLoading) return <div className="flex items-center justify-center h-32 text-gray-500">Loading…</div>;
  if (error) return <div className="p-4 text-red-600">Failed to load today's schedule.</div>;
  if (!data) return null;

  const active = data.appointments.filter((a) => a.status === "pending_confirmation" || a.status === "confirmed");
  const done = data.appointments.filter((a) => !["pending_confirmation", "confirmed"].includes(a.status));

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-gray-900">{data.tenant_name}</h1>
        <span className="text-sm text-gray-500">{data.date}</span>
      </div>

      {/* KPI Chips */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(data.counts)
          .filter(([, v]) => v > 0)
          .map(([k, v]) => (
            <span key={k} className={cn("px-2 py-0.5 rounded-full text-xs font-medium", STATUS_COLORS[k] ?? "bg-gray-100 text-gray-600")}>
              {STATUS_LABELS[k] ?? k}: {v}
            </span>
          ))}
      </div>

      {active.length === 0 && done.length === 0 && (
        <div className="text-center text-gray-400 py-12">No appointments today.</div>
      )}

      {active.map((appt) => (
        <div key={appt.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-2">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold text-gray-900">{appt.customer_name ?? "Unknown client"}</p>
              <p className="text-sm text-gray-500">{appt.service_name} · {appt.staff_name}</p>
              <p className="text-sm text-gray-600 font-medium">{formatTime(appt.start_at)}</p>
            </div>
            <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", STATUS_COLORS[appt.status] ?? "bg-gray-100 text-gray-600")}>
              {STATUS_LABELS[appt.status] ?? appt.status}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {appt.allowed_actions.filter((a) => a !== "reschedule").map((action) => (
              <button
                key={action}
                onClick={() => transition.mutate({ id: appt.id, action })}
                disabled={transition.isPending}
                className="text-xs px-3 py-1.5 bg-teal-600 text-white rounded-lg disabled:opacity-50"
              >
                {ACTION_LABELS[action] ?? action}
              </button>
            ))}
            <button
              onClick={() => navigate(`/tg/appointments/${appt.id}`)}
              className="text-xs px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg"
            >
              Detail
            </button>
          </div>
        </div>
      ))}

      {done.length > 0 && (
        <>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mt-4">Completed</h2>
          {done.map((appt) => (
            <div
              key={appt.id}
              className="bg-white rounded-xl border border-gray-100 p-3 flex items-center justify-between cursor-pointer"
              onClick={() => navigate(`/tg/appointments/${appt.id}`)}
            >
              <div>
                <p className="text-sm font-medium text-gray-800">{appt.customer_name}</p>
                <p className="text-xs text-gray-500">{appt.service_name} · {formatTime(appt.start_at)}</p>
              </div>
              <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", STATUS_COLORS[appt.status] ?? "bg-gray-100 text-gray-600")}>
                {STATUS_LABELS[appt.status] ?? appt.status}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
